import {
  Site,
  SitePatchDTO,
  useCreateSiteMutation,
  useDeleteSiteMutation,
  usePatchUpdateSiteMutation,
  useUpdateSiteMutation,
  useUpdateSiteTransportOilViaPipelineMutation,
  useUpdateSiteTransportViaPipelineMutation
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useApp} from "../providers/RegionProvider/RegionProvider"
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";
import {EntityMutationCallback} from "./useEntityMutation";

export type UseSiteUpdateProps = {}

export type UseSiteUpdateResult = {
  createSite: (site: Partial<Site>, callback: EntityMutationCallback<Site> | undefined) => void
  updateSite: (site: Partial<Site>, callback: EntityMutationCallback<Site> | undefined) => void
  patchUpdateSite: (id: string, patch: SitePatchDTO, callback: EntityMutationCallback<Site> | undefined) => void
  updateSiteTransportViaPipeline: (siteId: string, transportViaPipeline: boolean) => void
  updateSiteTransportOilViaPipeline: (siteId: string, transportViaPipeline: boolean) => void
  deleteSite: (id: string, callback: EntityMutationCallback | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useSiteMutation = (props: UseSiteUpdateProps = {}): UseSiteUpdateResult => {
  const {regionId} = useApp()
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createSite, {isLoading: _isCreating}] = useCreateSiteMutation()
  const [_updateSite, {isLoading: _isUpdating}] = useUpdateSiteMutation()
  const [_patchUpdateSite, {isLoading: _isPatchUpdating}] = usePatchUpdateSiteMutation()
  const [_updateSiteTransportViaPipelineMutation, {isLoading: _isUpdatingTransportViaPipeline}] = useUpdateSiteTransportViaPipelineMutation();
  const [_updateSiteTransportOilViaPipelineMutation, {isLoading: _isUpdatingTransportOilViaPipeline}] = useUpdateSiteTransportOilViaPipelineMutation();
  const [_deleteSite, {isLoading: _isDeleting}] = useDeleteSiteMutation()

  useEffect(() => {
    _setUpdating(_isUpdating || _isPatchUpdating || _isUpdatingTransportViaPipeline || _isUpdatingTransportOilViaPipeline)
  }, [_isUpdating, _isPatchUpdating, _isUpdatingTransportViaPipeline, _isUpdatingTransportOilViaPipeline, _setUpdating])

  useEffect(() => {
    _setCreating(_isCreating)
  }, [_isCreating, _setCreating])

  useEffect(() => {
    _setDeleting(_isDeleting)
  }, [_isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useSiteUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.site", "Site"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useSiteUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useSiteUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.site", "Site"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useSiteUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useSiteUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.site", "Site"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useSiteUpdate_deleting")
    }
  }, [_deleting, t]);

  const handlePromiseResult = useCallback((promise: Promise<ResultTypeFrom<any>>, title: string, callback: EntityMutationCallback<Site> | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: title,
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"}),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      )
  }, [t])

  const createSite = useCallback((site: Partial<Site>, callback: EntityMutationCallback<Site> | undefined) => {
    const promise = _createSite(site).unwrap()
    handlePromiseResult(
      promise,
      t("common.entity.site", "Site") + " " + t("common.status.created", "Created"),
      callback
    )
  }, [_createSite, handlePromiseResult, t])

  const updateSite = useCallback((site: Partial<Site>, callback: EntityMutationCallback<Site> | undefined) => {
    const promise = _updateSite(site).unwrap()
    handlePromiseResult(
      promise,
      t("common.entity.site", "Site") + " " + t("common.status.updated", "Updated"),
      callback
    )
  }, [_updateSite, handlePromiseResult, t])

  const patchUpdateSite = useCallback((id: string, patch: SitePatchDTO, callback: EntityMutationCallback<Site> | undefined) => {
    const promise = _patchUpdateSite({id, patch}).unwrap()
    handlePromiseResult(
      promise,
      t("common.entity.site", "Site") + " " + t("common.status.updated", "Updated"),
      callback
    )
  }, [_patchUpdateSite, handlePromiseResult, t])

  const _updateSiteTransportViaPipeline = useCallback((siteId: string, transportViaPipeline: boolean) => {
    if (regionId) {
      const promise = _updateSiteTransportViaPipelineMutation({
        siteId: siteId,
        regionId: regionId,
        transportViaPipeline: transportViaPipeline
      }).unwrap()
      handlePromiseResult(
        promise,
        t("common.entity.site", "Site") + " " + t("common.status.updated", "Updated"),
        undefined
      )
    }
  }, [_updateSiteTransportViaPipelineMutation, regionId, handlePromiseResult, t])

  const _updateSiteTransportOilViaPipeline = useCallback((siteId: string, transportViaPipeline: boolean) => {
    if (regionId) {
      const promise = _updateSiteTransportOilViaPipelineMutation({
        siteId: siteId,
        regionId: regionId,
        transportViaPipeline: transportViaPipeline
      }).unwrap()
      handlePromiseResult(
        promise,
        t("common.entity.site", "Site") + " " + t("common.status.updated", "Updated"),
        undefined
      )
    }
  }, [_updateSiteTransportOilViaPipelineMutation, regionId, handlePromiseResult, t])

  const handleDeleteConfirm = useCallback((id: string, callback: EntityMutationCallback | undefined) => {
    const promise = _deleteSite({id: id}).unwrap()
    handlePromiseResult(
      promise,
      t("common.entity.site", "Site") + " " + t("common.status.deleted", "Deleted"),
      callback
    )
  }, [_deleteSite, handlePromiseResult, t])

  const deleteSite = useCallback((id: string, callback: EntityMutationCallback | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.site", "Site"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.deletesite.confirm", "Are you sure you want to delete this site?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(id, callback)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    createSite: createSite,
    updateSite: updateSite,
    patchUpdateSite: patchUpdateSite,
    updateSiteTransportViaPipeline: _updateSiteTransportViaPipeline,
    updateSiteTransportOilViaPipeline: _updateSiteTransportOilViaPipeline,
    deleteSite: deleteSite,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
